import React, { useState, useEffect, useCallback } from "react";
import { database } from "../firebaseConfig";
import { ref, update, onValue } from "firebase/database";
import { getFunctions, httpsCallable } from "firebase/functions";
import "./MoreWorldSettings.css";

const functions = getFunctions();
const getCalendlyScheduledEvents = httpsCallable(functions, 'getCalendlyScheduledEvents');

const MoreWorldSettings = ({ userId }) => {
  const [moreSettings, setMoreSettings] = useState({
    calendlyKey: "",
    calendlyBookingLink: ""
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isSaved, setIsSaved] = useState(false);

  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState("");
  const [bookingsFilter, setBookingsFilter] = useState("all");

  useEffect(() => {
    if (!userId) return;
    
    const moreSettingsRef = ref(database, `/World/${userId}/settings/moreSettings`);
    const unsubscribeListener = onValue(
      moreSettingsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setMoreSettings({
            calendlyKey: data.calendlyKey || "",
            calendlyBookingLink: data.calendlyBookingLink || ""
          });
        }
      },
      (error) => {
        console.error("Error loading extra settings", error);
      }
    );

    return () => unsubscribeListener();
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMoreSettings(prev => ({
      ...prev,
      [name]: value
    }));
    setIsSaved(false);
  };

  const handleSave = async () => {
    if (!userId) {
      setMessage("User not found.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const moreSettingsRef = ref(database, `/World/${userId}/settings/moreSettings`);
      await update(moreSettingsRef, {
        calendlyKey: moreSettings.calendlyKey.trim(),
        calendlyBookingLink: moreSettings.calendlyBookingLink.trim()
      });
      
      setMessage("Calendly settings saved successfully!");
      setIsSaved(true);
      
      setTimeout(() => {
        setMessage("");
        setIsSaved(false);
      }, 3000);
    } catch (error) {
      console.error("Error saving extra settings:", error);
      setMessage("Error saving extra settings: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadBookings = useCallback(async () => {
    if (!moreSettings.calendlyKey) {
      setBookingsError("Please save your Calendly API Key first.");
      return;
    }

    setBookingsLoading(true);
    setBookingsError("");

    try {
      const result = await getCalendlyScheduledEvents({
        calendlyKey: moreSettings.calendlyKey,
        count: 20
      });

      if (result.data.success) {
        setBookings(result.data.events || []);
      } else {
        setBookingsError(result.data.error || "Failed to load bookings.");
      }
    } catch (err) {
      console.error("Error loading bookings:", err);
      setBookingsError("Could not load bookings. Please check your API key.");
    } finally {
      setBookingsLoading(false);
    }
  }, [moreSettings.calendlyKey]);

  useEffect(() => {
    if (moreSettings.calendlyKey) {
      loadBookings();
    }
  }, [moreSettings.calendlyKey, loadBookings]);

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = date.toDateString() === tomorrow.toDateString();

    const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

    if (isToday) return `Today, ${timeStr}`;
    if (isTomorrow) return `Tomorrow, ${timeStr}`;

    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }) + `, ${timeStr}`;
  };

  const getRelativeTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = date - now;
    const diffMins = Math.round(diffMs / 60000);
    const diffHours = Math.round(diffMs / 3600000);
    const diffDays = Math.round(diffMs / 86400000);

    if (diffMs > 0) {
      if (diffMins < 60) return `in ${diffMins}m`;
      if (diffHours < 24) return `in ${diffHours}h`;
      return `in ${diffDays}d`;
    } else {
      if (Math.abs(diffMins) < 60) return `${Math.abs(diffMins)}m ago`;
      if (Math.abs(diffHours) < 24) return `${Math.abs(diffHours)}h ago`;
      return `${Math.abs(diffDays)}d ago`;
    }
  };

  const filteredBookings = bookings.filter(event => {
    const now = new Date();
    const start = new Date(event.startTime);
    if (bookingsFilter === 'upcoming') return start > now;
    if (bookingsFilter === 'past') return start <= now;
    return true;
  });

  const isUpcoming = (isoString) => new Date(isoString) > new Date();

  const hasKey = !!moreSettings.calendlyKey;
  const upcomingCount = bookings.filter((e) => isUpcoming(e.startTime)).length;
  const lead = !hasKey
    ? 'No Calendly key yet. Add it so agents can book meetings and you can see upcoming appointments here.'
    : upcomingCount > 0
    ? `Calendly is connected. ${upcomingCount} upcoming booking${upcomingCount === 1 ? '' : 's'}.`
    : 'Calendly is connected. Bookings will show up here as soon as someone books.';

  return (
    <div className="cal">
      <header className="cal-page-head">
        <div>
          <h3 className="cal-page-title">Calendly</h3>
          <p className="cal-page-sub">
            Connect your calendar so agents can send your booking link and you can see who booked.
          </p>
        </div>
        <span className="cal-badge">{hasKey ? 'Connected' : 'Not connected'}</span>
      </header>

      <section className="cal-card">
        <div className={`cal-status ${!hasKey ? 'is-off' : ''}`}>
          <div className="cal-status-lead">
            <span className={`cal-status-dot ${hasKey ? 'on' : 'off'}`} aria-hidden />
            <span>{lead}</span>
          </div>
        </div>

        <div className="cal-field">
          <div className="cal-field-copy">
            <label htmlFor="calendlyKey">API key</label>
            <span>Used by agents to check availability and list bookings.</span>
          </div>
          <input
            id="calendlyKey"
            type="password"
            name="calendlyKey"
            value={moreSettings.calendlyKey}
            onChange={handleChange}
            className="cal-input"
            placeholder="Paste your Calendly API key"
            autoComplete="off"
          />
        </div>

        <div className="cal-field">
          <div className="cal-field-copy">
            <label htmlFor="calendlyBookingLink">Booking link</label>
            <span>Optional. The default link agents put into emails.</span>
          </div>
          <input
            id="calendlyBookingLink"
            type="url"
            name="calendlyBookingLink"
            value={moreSettings.calendlyBookingLink}
            onChange={handleChange}
            className="cal-input"
            placeholder="https://calendly.com/your-name/meeting"
          />
        </div>

        <div className="cal-actions">
          {message && (
            <span className={`cal-flash ${message.includes('successfully') ? 'is-ok' : 'is-err'}`}>
              {message.includes('successfully') ? 'Saved' : message}
            </span>
          )}
          <button
            type="button"
            className="altBtn altBtn--save"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving…' : isSaved ? 'Saved' : 'Save'}
          </button>
        </div>
      </section>

      {hasKey && (
        <section className="cal-card">
          <div className="cal-bookings-head">
            <div>
              <h4 className="cal-section-title">Recent bookings</h4>
              <p className="cal-section-sub">Pulled from Calendly with this key.</p>
            </div>
            <button
              type="button"
              className="altBtn altBtn--petrol"
              onClick={loadBookings}
              disabled={bookingsLoading}
            >
              {bookingsLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          <nav className="cal-tabs" role="tablist">
            {[
              { id: 'all', label: 'All' },
              { id: 'upcoming', label: 'Upcoming' },
              { id: 'past', label: 'Past' },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={bookingsFilter === t.id}
                className={`cal-tab ${bookingsFilter === t.id ? 'active' : ''}`}
                onClick={() => setBookingsFilter(t.id)}
              >
                {t.label}
              </button>
            ))}
          </nav>

          {bookingsError && <div className="cal-error">{bookingsError}</div>}

          {bookingsLoading && bookings.length === 0 ? (
            <div className="cal-empty">Loading bookings…</div>
          ) : filteredBookings.length === 0 && !bookingsError ? (
            <div className="cal-empty">
              {bookingsFilter === 'upcoming' ? 'No upcoming bookings.' : bookingsFilter === 'past' ? 'No past bookings.' : 'No bookings yet.'}
            </div>
          ) : (
            <ul className="cal-list">
              {filteredBookings.map((event, idx) => {
                const upcoming = isUpcoming(event.startTime);
                const locationLabel = event.location && event.location.location
                  ? (event.location.type === 'google_conference' ? 'Google Meet'
                    : event.location.type === 'zoom' ? 'Zoom'
                    : event.location.type === 'microsoft_teams_conference' ? 'MS Teams'
                    : event.location.type || 'Online')
                  : null;
                return (
                  <li key={event.uri || idx} className="cal-item">
                    <div className="cal-item-top">
                      <span className="cal-item-name">{event.name}</span>
                      <span className={`cal-chip ${upcoming ? '' : 'is-muted'}`}>
                        {upcoming ? 'Upcoming' : 'Done'}
                      </span>
                    </div>
                    <div className="cal-item-meta">
                      <span>{formatDateTime(event.startTime)}
                        {event.endTime && (
                          <> – {new Date(event.endTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</>
                        )}
                      </span>
                      <span>{getRelativeTime(event.startTime)}</span>
                      {locationLabel && <span>{locationLabel}</span>}
                    </div>
                    {event.invitees && event.invitees.length > 0 && (
                      <div className="cal-invitees">
                        {event.invitees.map((inv, i) => (
                          <div key={i} className="cal-invitee">
                            <span className="cal-invitee-name">{inv.name || 'Guest'}</span>
                            {inv.email && <span className="cal-invitee-email">{inv.email}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
};

export default MoreWorldSettings; 